import { memo } from 'react'
import { Link } from 'react-router-dom'
import Grading from '@mui/icons-material/Grading'
import {
  Card,
  CardActionArea,
  Stack,
  Typography,
  Button,
  useTheme,
  Box,
  Tooltip,
  CardActionAreaProps,
  BoxProps,
  Alert,
} from '@mui/material'
import { Project } from '@/types/projects'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import ProjectCardDropZone from '@/components/drop-zone/ProjectCardDropZone'
import LabeledProgressIndicator from '@/components/labeled-progress-indicator/LabeledProgressIndicator'
import TagChip from '@/components/tags/TagChip'
import FileUploadProvider from '@/components/upload-files/FileUploadProvider'

export type ProjectForCard = Project & {
  has_templates: boolean
  basic_workflow_id?: string
}

export type ProjectCardProps = {
  project: ProjectForCard
  inProgressCount: number
}

function ProjectCard({ project, inProgressCount }: ProjectCardProps) {
  const theme = useTheme()
  const { id: projectId, name, setup_state } = project

  const isSetupComplete = setup_state === 'complete'

  const Wrapper: React.FC<CardActionAreaProps & BoxProps> = isSetupComplete
    ? CardActionArea
    : Box

  const wrapperProps = isSetupComplete
    ? {
        LinkComponent: Link,
        to: projectId,
      }
    : {}

  const needsCompletion =
    setup_state !== 'complete' &&
    !setup_state.startsWith('creation_from_template')

  const hasPublishedModel = project.has_pub_models
  const basicWorkflowId = project.basic_workflow_id

  const content = (
    <Card sx={{ height: '100%', borderRadius: 2 }}>
      <Wrapper {...wrapperProps} sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
          spacing={2}
        >
          <Stack spacing={0.5} sx={{ width: '100%' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1">{name}</Typography>
              {inProgressCount > 0 && (
                <Tooltip
                  title={`${inProgressCount} document${
                    inProgressCount > 1 ? 's are' : ' is'
                  } waiting for review.`}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{inProgressCount}</Typography>
                    <Grading fontSize="small" />
                  </Stack>
                </Tooltip>
              )}
            </Stack>
            {setup_state === 'creation_from_template_started' && (
              <Stack
                justifyContent="space-between"
                direction="row"
                alignItems="center"
              >
                <LabeledProgressIndicator color="primary">
                  Building...
                </LabeledProgressIndicator>
                <CopyIDButton
                  asIconButton
                  stringToCopy={projectId}
                  size="small"
                  isSuperUser
                />
              </Stack>
            )}

            {setup_state === 'creation_from_template_failed' && (
              <Alert severity="error" variant="outlined">
                Creation of this project failed. Please contact support.
              </Alert>
            )}

            {needsCompletion && (
              <Typography
                variant="caption"
                color={theme.palette.text.secondary}
              >
                This project has not been fully configured. Complete the project
                setup to continue.
              </Typography>
            )}

            {(hasPublishedModel || project.has_templates) && (
              <Stack direction="row" spacing={1}>
                {hasPublishedModel && (
                  <Tooltip title="This project manages one or more published models.">
                    <Box sx={{ display: 'flex' }}>
                      <TagChip
                        tag={{
                          color: 'teal',
                          name: 'Published Model',
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
                {project.has_templates && (
                  <Tooltip title="A project template was created from this project.">
                    <Box sx={{ display: 'flex' }}>
                      <TagChip
                        tag={{
                          color: 'blue',
                          name: 'Template Project',
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            )}

            {needsCompletion && (
              <Stack spacing={2}>
                <Box>
                  <Button
                    variant="text"
                    component={Link}
                    to={`/project-setup/${setup_state}?project_id=${projectId}`}
                  >
                    Complete Setup
                  </Button>
                </Box>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Wrapper>
    </Card>
  )

  if (!basicWorkflowId) {
    return content
  }

  return (
    <FileUploadProvider project={project} workflowId={basicWorkflowId}>
      <ProjectCardDropZone project={project} enabled>
        {content}
      </ProjectCardDropZone>
    </FileUploadProvider>
  )
}

export default memo(ProjectCard)
